/**
 * Conn / ConnDot — realtime connection indicator (Claude Design `.conn`).
 *
 * `ConnDot` is just the 7px status dot: `live` (ok, with a pulsing ring),
 * `reconnect` (warn, blinking), `down` (danger, steady). `Conn` wraps it in the
 * bordered `surface-2` pill with a label.
 *
 *   <Conn status="live" />            → ● Live
 *   <Conn status="reconnect" label="Reconnecting" />
 *   <ConnDot status="down" />         → bare dot, e.g. inside a table cell
 */
import type { HTMLAttributes, ReactElement } from 'react';

import { cn } from './cn.js';

export type ConnStatus = 'live' | 'reconnect' | 'down';

const dotColor: Record<ConnStatus, string> = {
  live: 'bg-ok shadow-[0_0_8px_var(--hl-ok)]',
  reconnect: 'bg-warn shadow-[0_0_8px_var(--hl-warn)] animate-pulse',
  down: 'bg-danger',
};

const defaultLabel: Record<ConnStatus, string> = {
  live: 'Live',
  reconnect: 'Reconnecting',
  down: 'Offline',
};

export interface ConnDotProps extends HTMLAttributes<HTMLSpanElement> {
  status: ConnStatus;
}

export function ConnDot({ status, className, ...props }: ConnDotProps): ReactElement {
  return (
    <span
      aria-hidden="true"
      data-status={status}
      className={cn('relative inline-flex h-[7px] w-[7px] flex-shrink-0', className)}
      {...props}
    >
      {status === 'live' ? (
        <span className="absolute inset-0 animate-ping rounded-full border-[1.5px] border-ok opacity-60" />
      ) : null}
      <span className={cn('relative inline-block h-full w-full rounded-full', dotColor[status])} />
    </span>
  );
}

export interface ConnProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  status: ConnStatus;
  /** Override the label (defaults to Live / Reconnecting / Offline). */
  label?: string;
}

export function Conn({ status, label, className, ...props }: ConnProps): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      data-status={status}
      className={cn(
        'inline-flex h-8 flex-shrink-0 items-center gap-[7px] whitespace-nowrap rounded-md border border-border bg-surface-2 px-[11px]',
        'text-[11.5px] font-bold tracking-[0.04em] text-muted',
        className,
      )}
      {...props}
    >
      <ConnDot status={status} />
      {label ?? defaultLabel[status]}
    </div>
  );
}
